import { faExternalLinkSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import { Anchor } from "~/components/Anchor";
import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { Emdash } from "~/components/Punctuation";
import { AssetColumns } from "~/modules/assets/types";
import { parseCIK } from "~/utils/parseCIK";
import { AssetTableRowData } from "../types";
import { CatalogReferenceDataSlugs } from "~/modules/catalog/types";
import { Tooltip } from "~/components/Tooltip";

// // // //

function IssuerWithLeiOrCik(props: {
    item: AssetTableRowData;
    justify: "left" | "right";
}) {
    const { item, justify = "left" } = props;
    if (item.isAsset) {
        const issuer = item.issuer;
        if (!issuer) return <Emdash />;

        // Get LEI + CIK values
        const lei = issuer.lei;
        const cik = parseCIK(issuer.cik);

        // Just display name if there are no LEI or CIK values
        if (!lei && !cik) {
            return (
                <div className="dg-overflow">
                    {issuer.legal_name || issuer.name}
                </div>
            );
        }

        // Display CIK value w/ link
        if (issuer.cik) {
            return (
                <div
                    className={cn("flex flex-col justify-center w-full", {
                        "items-start": justify === "left",
                        "items-end": justify === "right",
                    })}
                >
                    <div className="flex items-center">
                        {issuer.legal_name || issuer.name}
                    </div>

                    <Tooltip
                        placement="top-start"
                        tooltipContent={
                            <div className="w-max px-3">
                                <p className="py-1.5 text-xs text-center">
                                    Click to view on SEC Edgar Search
                                </p>
                            </div>
                        }
                    >
                        {({ setTriggerRef }) => (
                            <Anchor
                                href={`https://www.sec.gov/edgar/search/#/entityName=${cik}`}
                                analyticsLabel="Asset Table - SEC Edgar CIK Lookup"
                                variant="default"
                                className="font-mono text-xs flex items-center gap-1"
                                openInNewTab
                                setTriggerRef={setTriggerRef}
                            >
                                {cik}
                                <FontAwesomeIcon
                                    icon={faExternalLinkSquare}
                                    className="h-3"
                                />
                            </Anchor>
                        )}
                    </Tooltip>
                </div>
            );
        }

        // Display LEI value w/ link
        return (
            <div className="flex items-start flex-col justify-center w-full overflow-hidden">
                <div className="dg-overflow">
                    {issuer.legal_name || issuer.name}
                </div>

                <Tooltip
                    placement="top-start"
                    tooltipContent={
                        <div className="w-max px-3">
                            <p className="py-1.5 text-xs text-center">
                                Click to view on LEI-Lookup
                            </p>
                        </div>
                    }
                >
                    {({ setTriggerRef }) => (
                        <Anchor
                            href={`https://www.lei-lookup.com/record/${lei}`}
                            analyticsLabel="Asset Table - LEI Lookup Link"
                            variant="default"
                            className="font-mono text-xs flex items-center gap-1 w-full"
                            openInNewTab
                            setTriggerRef={setTriggerRef}
                        >
                            <span className="dg-overflow">{lei}</span>
                            <FontAwesomeIcon
                                icon={faExternalLinkSquare}
                                className="h-3"
                            />
                        </Anchor>
                    )}
                </Tooltip>
            </div>
        );
    }

    return <Emdash />;
}

export const ISSUER_WITH_LEGAL_IDENTIFIER: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "issuer_with_legal_identifier",
    label: (
        <>
            {/* Let's try this even though its inconsistent? */}
            <p>Issuer Legal Name</p>
            <p className="text-xs">Legal Identifier</p>
        </>
    ),
    tooltip: "Legal identifier is the company's CIK or LEI registration number",
    type: ColumnTypes.string,
    sortsOn: AssetColumns.issuerName,
    width: 250,
    loginRequired: true,
    render: (item) => {
        return <IssuerWithLeiOrCik item={item} justify="left" />;
    },
};

export const ISSUER_WITH_LEGAL_IDENTIFIER_INLINE: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "issuer_with_legal_identifier",
    label: (
        <>
            {/* Let's try this even though its inconsistent? */}
            <p>Issuer Legal Name</p>
            <p className="text-xs">Legal Identifier</p>
        </>
    ),
    tooltip: "Legal identifier is the company's CIK or LEI registration number",
    type: ColumnTypes.string,
    sortsOn: AssetColumns.issuerName,
    width: 250,
    loginRequired: true,
    render: (item) => {
        return <IssuerWithLeiOrCik item={item} justify="left" />;
    },
};

export const ISSUER_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    slug: AssetColumns.issuerName,
    label: "Issuer",
    tooltip: CatalogReferenceDataSlugs.issuer,
    type: ColumnTypes.string,
    sortsOn: AssetColumns.issuerName,
    width: 410,
    cellValue: (item) => {
        if (item.isAsset) {
            if (!item.issuer) return null;
            return item.issuer?.name || "";
        }

        return null;
    },
};
